/**
 * @component ry-testimonial
 * @category  display
 * @summary   Customer testimonial / quote card — pure-display ry-ui-only component (no Fomantic equivalent). Renders a blockquote with optional avatar, attribution name + subtitle, and a star rating. Configured entirely via attributes; textContent becomes the quote.
 *
 * @attribute name      string?              Attribution name (e.g. "Jane Doe").
 * @attribute subtitle  string?              Attribution role / company text (e.g. "CTO, Acme Corp"). Use this instead of the deprecated `role` attribute (see gotchas).
 * @attribute avatar    string?              Avatar image URL. When set, renders a `<img>` with `alt` matching the name.
 * @attribute stars     number?   default=0  Number of filled star icons (0 to disable the rating block). Non-numeric or zero hides the stars row entirely.
 *
 * @content   The element's textContent becomes the quote text inside a `<blockquote>`. innerHTML is replaced at setup.
 *
 * @dom
 *   ry-testimonial[name?][subtitle?][avatar?][stars?]
 *     > div.ry-testimonial__avatar       [data-ry-target="avatar"]    (only when [avatar] is set)
 *       > img src=avatar alt=name
 *     > div.ry-testimonial__stars        [data-ry-target="stars"]     (only when [stars] > 0)
 *       > span.ry-testimonial__star × N
 *     > blockquote.ry-testimonial__quote [data-ry-target="quote"]    {quote text}
 *     > div.ry-testimonial__attribution  [data-ry-target="attribution"]
 *       > span.ry-testimonial__name      [data-ry-target="name"]      {name}
 *       > span.ry-testimonial__role      [data-ry-target="role"]      {subtitle} (only when [subtitle])
 *
 * @gotcha  Use `subtitle` for the job-title/byline text. The legacy `role` attribute is accepted for backwards compatibility but is dangerous — `role` is a reserved ARIA attribute and setting it to free-form text gives screen readers an invalid role. ry-testimonial WARNS when only the legacy `role` is set with no `subtitle`. Migrate to `subtitle`.
 *
 * @gotcha  Setup replaces innerHTML — any nested DOM in the original element is lost (only the textContent survives, lifted into the blockquote). To embed rich HTML, build the inner blockquote manually and skip ry-testimonial's transform (mount your own markup with `[data-ry-target="quote"]` and ry-testimonial will skip re-render).
 *
 * @gotcha  Avatar alt text is the `name` attribute. Without `[name]`, alt is empty (treated as decorative). Set `name` whenever a real photo is shown.
 *
 * @gotcha  Stars rendering is integer-only. `stars="3.5"` truncates to 3 — there is no half-star variant. For partial scores, use `<ry-rating value="3.5">` instead.
 *
 * @example basic
 * ```html
 * <ry-testimonial name="Jane Doe" subtitle="CTO, Acme Corp" avatar="https://i.pravatar.cc/64?img=5" stars="5">
 *   This changed everything for our team.
 * </ry-testimonial>
 * ```
 *
 * @example minimal
 * ```html
 * <ry-testimonial name="Anonymous">
 *   "It's fine."
 * </ry-testimonial>
 * ```
 *
 * @example no-avatar
 * ```html
 * <ry-testimonial name="J. Smith" subtitle="Engineer" stars="4">
 *   Solid, recommended.
 * </ry-testimonial>
 * ```
 *
 * @example no-stars
 * ```html
 * <ry-testimonial name="A. Reader" subtitle="VP Eng">
 *   The quote stands on its own.
 * </ry-testimonial>
 * ```
 *
 * @tutorial mount-lifts-textcontent-into-blockquote
 * ```js
 * const el = mount('<ry-testimonial name="X">My quote.</ry-testimonial>');
 * const q = el.querySelector('[data-ry-target="quote"]');
 * await expectEqual(q.textContent, 'My quote.', 'quote lifted from textContent');
 * await expectEqual(q.tagName, 'BLOCKQUOTE', 'rendered as a semantic blockquote');
 * ```
 *
 * @tutorial name-and-subtitle-render-attribution
 * ```js
 * const el = mount('<ry-testimonial name="Jane Doe" subtitle="CTO">Quote</ry-testimonial>');
 * await expectEqual(el.querySelector('[data-ry-target="name"]').textContent, 'Jane Doe');
 * await expectEqual(el.querySelector('[data-ry-target="role"]').textContent, 'CTO');
 * ```
 *
 * @tutorial no-subtitle-no-role-slot
 * ```js
 * // Wrong assumption: subtitle slot always renders. It's only inserted when
 * // the subtitle attribute is set (otherwise the attribution shows the name only).
 * const el = mount('<ry-testimonial name="X">q</ry-testimonial>');
 * await expectEqual(el.querySelector('[data-ry-target="role"]'), null, 'no role slot without subtitle');
 * ```
 *
 * @tutorial avatar-renders-img-with-alt-as-name
 * ```js
 * const el = mount('<ry-testimonial name="Jane" avatar="/j.jpg">q</ry-testimonial>');
 * const avatarSlot = el.querySelector('[data-ry-target="avatar"]');
 * await expectEqual(!!avatarSlot, true);
 * const img = avatarSlot.querySelector('img');
 * await expectAttr(img, 'src', '/j.jpg');
 * await expectAttr(img, 'alt', 'Jane', 'alt matches name');
 * ```
 *
 * @tutorial no-avatar-no-slot
 * ```js
 * const el = mount('<ry-testimonial name="X">q</ry-testimonial>');
 * await expectEqual(el.querySelector('[data-ry-target="avatar"]'), null);
 * ```
 *
 * @tutorial stars-render-correct-count
 * ```js
 * const el = mount('<ry-testimonial name="X" stars="3">q</ry-testimonial>');
 * const stars = el.querySelectorAll('.ry-testimonial__star');
 * await expectEqual(stars.length, 3);
 * ```
 *
 * @tutorial zero-or-missing-stars-omits-slot
 * ```js
 * const a = mount('<ry-testimonial name="X" stars="0">q</ry-testimonial>');
 * await expectEqual(a.querySelector('[data-ry-target="stars"]'), null, 'stars="0" omits slot');
 * const b = mount('<ry-testimonial name="X">q</ry-testimonial>');
 * await expectEqual(b.querySelector('[data-ry-target="stars"]'), null, 'missing stars omits slot');
 * ```
 *
 * @tutorial legacy-role-attribute-warns
 * ```js
 * // Wrong assumption: [role="CTO"] is the right way to set the job title.
 * // role is an ARIA attribute — using it for free text breaks accessibility.
 * // ry-testimonial reads it as a fallback for [subtitle] but warns.
 * const warnings = [];
 * const orig = console.warn;
 * console.warn = (...a) => { warnings.push(a.join(' ')); };
 * try {
 *   mount('<ry-testimonial name="J" role="CTO">q</ry-testimonial>');
 * } finally {
 *   console.warn = orig;
 * }
 * await expectEqual(warnings.some(w => w.includes('subtitle')), true, 'warning mentions subtitle');
 * ```
 *
 * @see ry-card
 * @see ry-rating
 */

import { RyElement } from '../core/ry-element.js';
import { getIcon } from '../core/ry-icons.js';

export class RyTestimonial extends RyElement {
  setup(): void {
    this.#transformMarkup();
  }

  #transformMarkup(): void {
    if (this.$('[data-ry-target="quote"]')) return;

    const avatar = this.getAttribute('avatar') ?? '';
    const name = this.getAttribute('name') ?? '';

    let subtitle = this.getAttribute('subtitle') ?? '';
    const legacyRole = this.getAttribute('role');
    // Treat `role` as legacy subtitle ONLY when it isn't a valid ARIA role.
    // Hard to enumerate every valid ARIA role here — but a job-title string
    // is unlikely to be `button` / `region` / `list` / etc., so we use a
    // simple heuristic: if it contains a space, comma, or uppercase, it's
    // almost certainly free text, not an ARIA role.
    const looksLikeFreeText = legacyRole && /[\s,]|[A-Z]/.test(legacyRole);
    if (!subtitle && looksLikeFreeText) {
      subtitle = legacyRole;
      // eslint-disable-next-line no-console
      console.warn(
        '[ry-testimonial] use [subtitle] instead of [role] for the byline — [role] is an ARIA attribute and using it for free text breaks accessibility',
        this
      );
    }

    const stars = parseInt(this.getAttribute('stars') ?? '0', 10);
    const quoteText = this.textContent?.trim() ?? '';

    const avatarHTML = avatar
      ? `<div data-ry-target="avatar" class="ry-testimonial__avatar"><img src="${avatar}" alt="${name}"></div>`
      : '';

    const starsHTML = stars > 0
      ? `<div data-ry-target="stars" class="ry-testimonial__stars">${Array.from({ length: stars }, () => `<span class="ry-testimonial__star">${getIcon('star')}</span>`).join('')}</div>`
      : '';

    this.innerHTML = `
      ${avatarHTML}
      ${starsHTML}
      <blockquote data-ry-target="quote" class="ry-testimonial__quote">${quoteText}</blockquote>
      <div data-ry-target="attribution" class="ry-testimonial__attribution">
        <span data-ry-target="name" class="ry-testimonial__name">${name}</span>
        ${subtitle ? `<span data-ry-target="role" class="ry-testimonial__role">${subtitle}</span>` : ''}
      </div>
    `;
  }
}

customElements.define('ry-testimonial', RyTestimonial);
